import { AVAILABLE_MODELS, ModelName, getClientForModel, DEFAULT_MODEL, isOpenAIClient, isAnthropicClient } from './modelConfig';

interface CompletionResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

const getCompletionContent = (completion: CompletionResponse | null): string => {
  const content = completion?.choices[0]?.message?.content;
  if (!content) {
    throw new Error('No completion content received');
  }
  return content;
};

export const generateIdeas = async (topic: string, modelName: ModelName = DEFAULT_MODEL): Promise<string> => {
  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a creative brainstorming assistant. Provide concise, clear ideas."
        },
        {
          role: "user",
          content: `Generate 5 unique ideas related to: ${topic}. Keep each idea brief and clear.`
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(200, model.maxTokens),
    }) as CompletionResponse;

    return getCompletionContent(completion);
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(200, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: `Generate 5 unique ideas related to: ${topic}. Keep each idea brief and clear.`
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text : '';
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
};

export const expandIdea = async (idea: string, modelName: ModelName = DEFAULT_MODEL): Promise<string> => {
  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a creative writer skilled at expanding ideas into engaging narratives. Provide detailed, well-structured content that explores the concept thoroughly."
        },
        {
          role: "user",
          content: `Please expand this idea into a detailed narrative (1-2 pages). Include context, implications, and practical applications where relevant: "${idea}"`
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(1000, model.maxTokens),
    }) as CompletionResponse;

    return getCompletionContent(completion);
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(1000, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: `Please expand this idea into a detailed narrative (1-2 pages). Include context, implications, and practical applications where relevant: "${idea}"`
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text : '';
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
};

export const generateSimilarIdeas = async (idea: string, existingIdeas: string[] = [], modelName: ModelName = DEFAULT_MODEL): Promise<string> => {
  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  const existingIdeasText = existingIdeas.length > 0 
    ? `\n\nExisting ideas to avoid duplicating:\n${existingIdeas.map(i => `• ${i}`).join('\n')}`
    : '';

  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a creative brainstorming assistant. Provide unique, non-repetitive ideas that build upon but are distinct from existing ones. Format each idea with a title followed by a brief description on the same line."
        },
        {
          role: "user",
          content: `Generate 5 unique ideas related to: ${idea}. 

Format each idea as a single line:
• **[Title]**: Brief description of the idea

Each idea should be different from these existing ideas:${existingIdeasText}

Keep descriptions brief and ensure they are distinct from both the original idea and any existing ideas listed.`
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(250, model.maxTokens),
    }) as CompletionResponse;

    const content = getCompletionContent(completion);
  
    // Ensure consistent formatting
    return content
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        // Ensure each line starts with a bullet point
        if (!line.startsWith('•')) {
          return `• ${line}`;
        }
        return line;
      })
      .join('\n');
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(250, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: `Generate 5 unique ideas related to: ${idea}. 

Format each idea as a single line:
• **[Title]**: Brief description of the idea

Each idea should be different from these existing ideas:${existingIdeasText}

Keep descriptions brief and ensure they are distinct from both the original idea and any existing ideas listed.`
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text : '';
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
};

export const rewriteIdea = async (idea: string, prompt: string, modelName: ModelName = DEFAULT_MODEL): Promise<string> => {
  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a creative writer skilled at rewriting and reformulating ideas based on specific requirements. If you receive text with {{selection}}, only rewrite the selected portion while preserving the rest of the text exactly as is."
        },
        {
          role: "user",
          content: `Please rewrite this text: "${idea}" following this instruction: ${prompt}`
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(200, model.maxTokens),
    }) as CompletionResponse;

    return getCompletionContent(completion);
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(200, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: `Please rewrite this text: "${idea}" following this instruction: ${prompt}`
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text : '';
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
};

export const generateTopics = async (modelName: ModelName = DEFAULT_MODEL): Promise<string[]> => {
  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a creative brainstorming assistant. Generate 6 trending and innovative topics for ideation."
        },
        {
          role: "user",
          content: "Generate 6 unique, trending topics that people might want to brainstorm about. Topics should be forward-thinking and relevant to current global challenges and opportunities. Keep each topic concise (2-4 words)."
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(200, model.maxTokens),
    }) as CompletionResponse;

    const content = getCompletionContent(completion);
    return content
      .split('\n')
      .filter(topic => topic.trim())
      .map(topic => topic.replace(/^\d+\.\s*/, '').trim()) // Remove numbering if present
      .slice(0, 6); // Ensure we only get 6 topics
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(200, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: "Generate 6 unique, trending topics that people might want to brainstorm about. Topics should be forward-thinking and relevant to current global challenges and opportunities. Keep each topic concise (2-4 words)."
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text.split('\n').slice(0, 6) : [];
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
};

interface SynthesisIdea {
  content: string;
  parentChain: string[];
}

const SYNTHESIS_PROMPTS = {
  research: `As an academic researcher, create a comprehensive research paper (minimum 4 pages) that synthesizes these ideas into a cohesive narrative. 

Your paper should include:
1. An engaging abstract that captures the essence of the research
2. A thorough introduction setting the context and significance
3. A detailed literature review connecting these ideas to existing research
4. A comprehensive methodology section
5. In-depth analysis and discussion that explores implications
6. Real-world applications and future directions
7. A conclusion that ties everything together

Use an academic yet engaging writing style. Expand each section with rich detail, examples, and theoretical frameworks. Consider the relationships between ideas and develop compelling arguments.

Ideas to synthesize:
{ideas}

{relationships}

Remember to maintain academic rigor while telling a compelling story about these interconnected concepts.`,

  product: `As a product visionary, craft a detailed product document (minimum 4 pages) that transforms these ideas into a compelling product narrative.

Your document should include:
1. An executive summary that paints the vision
2. A rich product narrative describing the user journey and experience
3. Detailed feature explorations with user scenarios and stories
4. Technical architecture and implementation considerations
5. Market analysis and competitive positioning
6. Development roadmap and milestones
7. Risk analysis and mitigation strategies

Focus on storytelling while maintaining technical depth. Bring the product to life through detailed scenarios and user stories.

Ideas to synthesize:
{ideas}

{relationships}

Remember to balance visionary thinking with practical implementation details.`,

  prfaq: `As a product storyteller, create an extensive Press Release and FAQ document (minimum 4 pages) that presents these ideas as a revolutionary solution.

Structure your document as follows:
1. A compelling press release that:
   - Hooks the reader with an engaging headline
   - Tells the story of the problem and solution
   - Includes rich customer testimonials and use cases
   - Provides meaningful statistics and market impact
   - Ends with a powerful call to action

2. A comprehensive FAQ section that:
   - Anticipates and answers detailed questions about the solution
   - Explores technical details and implementation
   - Addresses market positioning and competitive advantages
   - Discusses future roadmap and evolution
   - Covers pricing, availability, and support

Use a narrative style that builds excitement while providing depth and substance.

Ideas to synthesize:
{ideas}

{relationships}

Focus on creating a compelling narrative that educates and inspires.`,

  prd: `As a product requirements storyteller, create a detailed PRD (minimum 4 pages) that weaves these ideas into a comprehensive product narrative.

Your document should include:
1. A compelling product vision and strategy
2. Detailed user personas and journey maps
3. Comprehensive user stories and acceptance criteria
4. In-depth feature specifications with:
   - User interaction flows
   - Technical requirements
   - Edge cases and constraints
   - Success metrics
5. Implementation considerations and dependencies
6. Timeline and prioritization framework
7. Risk assessment and mitigation strategies

Use a narrative approach to connect requirements to user needs and business goals.

Ideas to synthesize:
{ideas}

{relationships}

Remember to maintain technical precision while telling the story of how this product will impact users' lives.`,

  slides: `As a presentation designer, create a detailed slide deck that transforms these ideas into an engaging presentation. Each slide should be concise yet informative.

Format each slide using clean, professional markdown:

# Opening
**The Future of Innovation**
Transforming ideas into market-leading solutions
*A new approach to product development*
2024 Strategic Initiative

Speaker Notes:
Opening narrative and key points to emphasize

Suggested Visuals:
Hero image showing innovation concept

# Market Overview
**Current Landscape**
$50B total addressable market
*Growing at 25% annually*
3 key segments driving growth

Speaker Notes:
Market analysis and growth trends

Suggested Visuals:
Market size pie chart, growth trend line

[Continue with similarly formatted slides...]

Core sections to cover:
1. Opening Impact
   - Vision statement
   - Key opportunity
   - Core value proposition

2. Market Context
   - Size and growth
   - Key trends
   - Competition

3. Solution Overview
   - Core capabilities
   - Key benefits
   - Differentiators

4. Implementation
   - Approach
   - Timeline
   - Requirements

5. Business Case
   - ROI metrics
   - Success factors
   - Validation

6. Next Steps
   - Key decisions
   - Action items
   - Timeline

For each slide:
- Start with a clear headline
- Use 3-4 key points maximum
- Keep content concise and impactful
- Use bold for main points
- Use italic for emphasis
- Avoid bullet points at top level
- Ensure all content fits without scrolling

Ideas to synthesize:
{ideas}

{relationships}

Focus on creating a clear, impactful narrative that drives understanding and action while maintaining visual clarity.`
};

export const synthesizeIdeas = async (
  ideas: SynthesisIdea[],
  format: 'research' | 'product' | 'prfaq' | 'prd' | 'slides',
  modelName: ModelName = DEFAULT_MODEL
): Promise<string> => {
  // Format ideas and their relationships
  const formattedIdeas = ideas.map(idea => `• ${idea.content}`).join('\n');
  const relationships = ideas
    .filter(idea => idea.parentChain.length > 0)
    .map(idea => `• ${idea.content} builds upon: ${idea.parentChain.join(' → ')}`)
    .join('\n');

  const prompt = SYNTHESIS_PROMPTS[format]
    .replace('{ideas}', formattedIdeas)
    .replace('{relationships}', relationships);

  const model = AVAILABLE_MODELS[modelName];
  const client = getClientForModel(modelName);
  
  if (isOpenAIClient(client)) {
    const completion = await client.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are a professional document writer skilled in creating detailed, narrative-driven documents that tell compelling stories while maintaining technical depth and precision."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      model: model.name,
      temperature: model.temperature,
      max_tokens: Math.min(4000, model.maxTokens),
    }) as CompletionResponse;

    return getCompletionContent(completion);
  } else if (isAnthropicClient(client)) {
    const message = await client.messages.create({
      model: model.name,
      max_tokens: Math.min(4000, model.maxTokens),
      temperature: model.temperature,
      messages: [{
        role: 'user',
        content: prompt
      }]
    });

    return message.content[0].type === 'text' ? message.content[0].text : '';
  }

  throw new Error(`Unsupported model provider: ${model.provider}`);
}; 